/**
 * Backoffice DI Registry
 *
 * App-specific dependency registration.
 * This file should be imported FIRST in app.ts to initialize
 * the DI container before any components load.
 */

import { DiContainer } from "ui-common/di";

import { CounterState } from "ui-common/services/counter/counter-state";
import { CounterService } from "ui-common/services/counter/counter-service";
import { SseState } from "ui-common/services/sse/sse-state";
import { SseService } from "ui-common/services/sse/sse-service";
import { RpcService } from "ui-common/services/rpc/rpc-service";
import { LiveChatState } from "ui-common/services/live-chat/chat-state";
import { LiveChatService } from "ui-common/services/live-chat/chat-service";
import { AuthState } from "ui-common/services/auth/auth-state";
import { AuthService } from "ui-common/services/auth/auth-service";
import { AuthProvider } from "ui-common/services/auth/auth-provider";
import type { ISseState } from "ui-common/services/sse/types";
import type { ILiveChatState } from "ui-common/services/live-chat/types";
import type { IAuthState } from "ui-common/services/auth/types";
import { NotificationTranslationService } from "ui-common/services/notification-translation/notification-translation-service";
import { NotificationsState } from "ui-common/services/notifications/notifications-state";
import { NotificationsService } from "ui-common/services/notifications/notifications-service";
import type { INotificationsState } from "ui-common/services/notifications/types";
import { CreatorProfileDataState } from "../services/creator-profile-data/creator-profile-data-state";
import { CreatorProfileDataService } from "../services/creator-profile-data/creator-profile-data-service";
import type { ICreatorProfileDataState } from "../services/creator-profile-data/types";
import { CreatorLiveEventsState } from "../services/creator-live-events/creator-live-events-state";
import { CreatorLiveEventsService } from "../services/creator-live-events/creator-live-events-service";
import type { ICreatorLiveEventsState } from "../services/creator-live-events/types";
import {
  LiveEventFormState,
  asReadonlyLiveEventFormState,
} from "ui-common/services/live-event-form/live-event-form-state";
import { LiveEventFormService } from "ui-common/services/live-event-form/live-event-form-service";
import { BroadcasterDiagnosticsState } from "ui-common/services/broadcaster-diagnostics/broadcaster-diagnostics-state";
import { BroadcasterDiagnosticsService } from "ui-common/services/broadcaster-diagnostics/broadcaster-diagnostics-service";
import type { IBroadcasterDiagnosticsState } from "ui-common/services/broadcaster-diagnostics/types";
import { PreviewState } from "ui-common/services/preview/preview-state";
import type { IPreviewState } from "ui-common/services/preview/types";
import { BackofficeFeatureFlagsState } from "../services/backoffice-feature-flags/backoffice-feature-flags-state";
import { BackofficeFeatureFlagsService } from "../services/backoffice-feature-flags/backoffice-feature-flags-service";
import type { IBackofficeFeatureFlagsState } from "../services/backoffice-feature-flags/types";
import { SubscriptionTierState } from "../services/subscription-tier/subscription-tier-state";
import { SubscriptionTierService } from "../services/subscription-tier/subscription-tier-service";
import type { ISubscriptionTierState } from "../services/subscription-tier/types";
import { BackofficeWorldsState } from "../services/backoffice-worlds/backoffice-worlds-state";
import { BackofficeWorldsService } from "../services/backoffice-worlds/backoffice-worlds-service";
import type { IBackofficeWorldsState } from "../services/backoffice-worlds/types";
import { BackofficeLinksState } from "../services/backoffice-links/backoffice-links-state";
import { BackofficeLinksService } from "../services/backoffice-links/backoffice-links-service";
import type { IBackofficeLinksState } from "../services/backoffice-links/types";
import { BackofficeBrandsState } from "../services/backoffice-brands/backoffice-brands-state";
import { BackofficeBrandsService } from "../services/backoffice-brands/backoffice-brands-service";
import type { IBackofficeBrandsState } from "../services/backoffice-brands/types";
import { BackofficePostsState } from "../services/backoffice-posts/backoffice-posts-state";
import { BackofficePostsService } from "../services/backoffice-posts/backoffice-posts-service";
import type { IBackofficePostsState } from "../services/backoffice-posts/types";
import { AnalyticsDataState } from "../services/analytics-data/analytics-data-state";
import { AnalyticsDataService } from "../services/analytics-data/analytics-data-service";
import type { IAnalyticsDataState } from "../services/analytics-data/types";
import { ExploreCreatorsState } from "ui-common/services/explore-creators/explore-creators-state";
import { ExploreCreatorsService } from "ui-common/services/explore-creators/explore-creators-service";
import type { IExploreCreatorsState } from "ui-common/services/explore-creators/types";

/**
 * Type-safe key constants.
 * Use these when registering and retrieving dependencies.
 */
export const KEYS = {
  counterState: "counterState",
  counterService: "counterService",
  sseState: "sseState",
  writableSseState: "writableSseState",
  sseService: "sseService",
  rpcService: "rpcService",
  liveChatState: "liveChatState",
  writableLiveChatState: "writableLiveChatState",
  liveChatService: "liveChatService",
  creatorProfileDataState: "creatorProfileDataState",
  writableCreatorProfileDataState: "writableCreatorProfileDataState",
  creatorProfileDataService: "creatorProfileDataService",
  notificationsState: "notificationsState",
  writableNotificationsState: "writableNotificationsState",
  notificationsService: "notificationsService",
  notificationTranslationService: "notificationTranslationService",
  authState: "authState",
  writableAuthState: "writableAuthState",
  authService: "authService",
  authProvider: "authProvider",
  creatorLiveEventsState: "creatorLiveEventsState",
  writableCreatorLiveEventsState: "writableCreatorLiveEventsState",
  creatorLiveEventsService: "creatorLiveEventsService",
  liveEventFormState: "liveEventFormState",
  writableLiveEventFormState: "writableLiveEventFormState",
  liveEventFormService: "liveEventFormService",
  broadcasterDiagnosticsState: "broadcasterDiagnosticsState",
  writableBroadcasterDiagnosticsState: "writableBroadcasterDiagnosticsState",
  broadcasterDiagnosticsService: "broadcasterDiagnosticsService",
  previewState: "previewState",
  backofficeFeatureFlagsState: "backofficeFeatureFlagsState",
  writableBackofficeFeatureFlagsState: "writableBackofficeFeatureFlagsState",
  backofficeFeatureFlagsService: "backofficeFeatureFlagsService",
  subscriptionTierState: "subscriptionTierState",
  writableSubscriptionTierState: "writableSubscriptionTierState",
  subscriptionTierService: "subscriptionTierService",
  backofficeWorldsState: "backofficeWorldsState",
  writableBackofficeWorldsState: "writableBackofficeWorldsState",
  backofficeWorldsService: "backofficeWorldsService",
  backofficeLinksState: "backofficeLinksState",
  writableBackofficeLinksState: "writableBackofficeLinksState",
  backofficeLinksService: "backofficeLinksService",
  backofficeBrandsState: "backofficeBrandsState",
  writableBackofficeBrandsState: "writableBackofficeBrandsState",
  backofficeBrandsService: "backofficeBrandsService",
  backofficePostsState: "backofficePostsState",
  writableBackofficePostsState: "writableBackofficePostsState",
  backofficePostsService: "backofficePostsService",
  analyticsDataState: "analyticsDataState",
  writableAnalyticsDataState: "writableAnalyticsDataState",
  analyticsDataService: "analyticsDataService",
  exploreCreatorsState: "exploreCreatorsState",
  writableExploreCreatorsState: "writableExploreCreatorsState",
  exploreCreatorsService: "exploreCreatorsService",
} as const;

/**
 * Initialize the DI container with all dependencies.
 * Call this FIRST in app.ts before any other imports that use injection.
 */
export function initializeDi(): void {
  // Create instances
  const counterState = new CounterState();
  const counterService = new CounterService();
  const sseState = new SseState();
  const sseService = new SseService();
  const rpcService = new RpcService();
  const liveChatState = new LiveChatState();
  const liveChatService = new LiveChatService();
  const creatorProfileDataState = new CreatorProfileDataState();
  const creatorProfileDataService = new CreatorProfileDataService();
  const notificationsState = new NotificationsState();
  const notificationsService = new NotificationsService();
  const notificationTranslationService = new NotificationTranslationService();
  const authState = new AuthState();
  const authService = new AuthService();
  const authProvider = new AuthProvider();
  const creatorLiveEventsState = new CreatorLiveEventsState();
  const creatorLiveEventsService = new CreatorLiveEventsService();
  const liveEventFormState = new LiveEventFormState();
  const liveEventFormService = new LiveEventFormService();
  const broadcasterDiagnosticsState = new BroadcasterDiagnosticsState();
  const broadcasterDiagnosticsService = new BroadcasterDiagnosticsService();
  const previewState = new PreviewState();
  const backofficeFeatureFlagsState = new BackofficeFeatureFlagsState();
  const backofficeFeatureFlagsService = new BackofficeFeatureFlagsService();
  const subscriptionTierState = new SubscriptionTierState();
  const subscriptionTierService = new SubscriptionTierService();
  const backofficeWorldsState = new BackofficeWorldsState();
  const backofficeWorldsService = new BackofficeWorldsService();
  const backofficeLinksState = new BackofficeLinksState();
  const backofficeLinksService = new BackofficeLinksService();
  const backofficeBrandsState = new BackofficeBrandsState();
  const backofficeBrandsService = new BackofficeBrandsService();
  const backofficePostsState = new BackofficePostsState();
  const backofficePostsService = new BackofficePostsService();
  const analyticsDataState = new AnalyticsDataState();
  const analyticsDataService = new AnalyticsDataService();
  const exploreCreatorsState = new ExploreCreatorsState();
  const exploreCreatorsService = new ExploreCreatorsService();

  // Initialize container with all dependencies
  DiContainer.initialize({
    [KEYS.counterState]: counterState,
    [KEYS.counterService]: counterService,
    [KEYS.sseState]: sseState as ISseState,
    [KEYS.writableSseState]: sseState,
    [KEYS.sseService]: sseService,
    [KEYS.rpcService]: rpcService,
    [KEYS.liveChatState]: liveChatState as ILiveChatState,
    [KEYS.writableLiveChatState]: liveChatState,
    [KEYS.liveChatService]: liveChatService,
    [KEYS.creatorProfileDataState]: creatorProfileDataState as ICreatorProfileDataState,
    [KEYS.writableCreatorProfileDataState]: creatorProfileDataState,
    [KEYS.creatorProfileDataService]: creatorProfileDataService,
    [KEYS.notificationsState]: notificationsState as INotificationsState,
    [KEYS.writableNotificationsState]: notificationsState,
    [KEYS.notificationsService]: notificationsService,
    [KEYS.notificationTranslationService]: notificationTranslationService,
    [KEYS.authState]: authState as IAuthState,
    [KEYS.writableAuthState]: authState,
    [KEYS.authService]: authService,
    [KEYS.authProvider]: authProvider,
    [KEYS.creatorLiveEventsState]: creatorLiveEventsState as ICreatorLiveEventsState,
    [KEYS.writableCreatorLiveEventsState]: creatorLiveEventsState,
    [KEYS.creatorLiveEventsService]: creatorLiveEventsService,
    [KEYS.liveEventFormState]: asReadonlyLiveEventFormState(liveEventFormState),
    [KEYS.writableLiveEventFormState]: liveEventFormState,
    [KEYS.liveEventFormService]: liveEventFormService,
    [KEYS.broadcasterDiagnosticsState]: broadcasterDiagnosticsState as IBroadcasterDiagnosticsState,
    [KEYS.writableBroadcasterDiagnosticsState]: broadcasterDiagnosticsState,
    [KEYS.broadcasterDiagnosticsService]: broadcasterDiagnosticsService,
    [KEYS.previewState]: previewState as IPreviewState,
    [KEYS.backofficeFeatureFlagsState]: backofficeFeatureFlagsState as IBackofficeFeatureFlagsState,
    [KEYS.writableBackofficeFeatureFlagsState]: backofficeFeatureFlagsState,
    [KEYS.backofficeFeatureFlagsService]: backofficeFeatureFlagsService,
    [KEYS.subscriptionTierState]: subscriptionTierState as ISubscriptionTierState,
    [KEYS.writableSubscriptionTierState]: subscriptionTierState,
    [KEYS.subscriptionTierService]: subscriptionTierService,
    [KEYS.backofficeWorldsState]: backofficeWorldsState as IBackofficeWorldsState,
    [KEYS.writableBackofficeWorldsState]: backofficeWorldsState,
    [KEYS.backofficeWorldsService]: backofficeWorldsService,
    [KEYS.backofficeLinksState]: backofficeLinksState as IBackofficeLinksState,
    [KEYS.writableBackofficeLinksState]: backofficeLinksState,
    [KEYS.backofficeLinksService]: backofficeLinksService,
    [KEYS.backofficeBrandsState]: backofficeBrandsState as IBackofficeBrandsState,
    [KEYS.writableBackofficeBrandsState]: backofficeBrandsState,
    [KEYS.backofficeBrandsService]: backofficeBrandsService,
    [KEYS.backofficePostsState]: backofficePostsState as IBackofficePostsState,
    [KEYS.writableBackofficePostsState]: backofficePostsState,
    [KEYS.backofficePostsService]: backofficePostsService,
    [KEYS.analyticsDataState]: analyticsDataState as IAnalyticsDataState,
    [KEYS.writableAnalyticsDataState]: analyticsDataState,
    [KEYS.analyticsDataService]: analyticsDataService,
    [KEYS.exploreCreatorsState]: exploreCreatorsState as IExploreCreatorsState,
    [KEYS.writableExploreCreatorsState]: exploreCreatorsState,
    [KEYS.exploreCreatorsService]: exploreCreatorsService,
  });

  // Initialize services that need it
  counterService.initialize();
  sseService.initialize();
  authService.initialize();
  broadcasterDiagnosticsService.initialize();

  if (location.hostname === "localhost") {
    console.log("[DI] Backoffice container initialized with keys:", DiContainer.keys());
  }
}
